<template>
  <div class="content-item">
    <a-row>
      <a-col :span="24">
        <a class="open-instruction-video" href="#" @click.prevent="e => openInstructionVideo(introVideo)">
          <inline-svg :src="require('../../assets/images/video-capture.svg')" width="18" height="12" />
          <span>View instruction video again</span>
        </a>
      </a-col>
    </a-row>
    <a-row class="mt-3">
      <a-col :xs="{ span: 24, offset: 0 }" :sm="{ span: 20, offset: 2 }" :md="{ span: 16, offset: 4 }" :xl="{ span: 12, offset: 0 }">
        <h4 class="text-left pl-4" v-if="(theme = 'dark')">1. Watch your story, keep video playing for step 2</h4>
        <h4 class="text-left pl-4" v-else>1. Preview your {{ videoSubtitle }}</h4>
        <a-row class="mt-3">
          <a-col :span="24">
            <div class="responsive-embed" :class="{ placeholder: !embedCode }" v-html="embedCode"></div>
          </a-col>
        </a-row>
        <a-row class="mt-1 mb-4">
          <a-col class="slug-input-wrapper pl-3">
            <span class="slug-input-label">{{ $t('label.embedVideoUrl') }}</span>
            <a-input size="default" v-model="embedUrl" />
          </a-col>
        </a-row>
      </a-col>
      <a-col :xs="{ span: 24, offset: 0 }" :sm="{ span: 20, offset: 2 }" :md="{ span: 16, offset: 4 }" :xl="{ span: 12, offset: 0 }">
        <h4 class="text-left pl-4" v-if="(theme === 'dark')">2. Click gold button to select screenshot while video plays.</h4>
        <h4 class="text-left pl-4" v-else>2. Click blue button to select screenshot while video plays.</h4>
        <a-row class="thumbnail-area">
          <a-col :span="24" v-show="thumbnail">
            <div class="responsive-embed">
              <img v-if="thumbnail" :src="thumbnail" />
            </div>
          </a-col>
        </a-row>
        <a-row class="mt-4 mb-4">
          <a-col :span="12" class="w-full">
            <input class="thumbnail-input" type="file" :multiple="false" :accept="'image/jpeg, image/png'" ref="thumbnailInput" @change="thumbnailChanged" />
            <a-button type="primary" class="action-button take-thumbnail tf-btn tf-btn-primary" @click.prevent="onSelect">
              <span class="px-4">Upload Picture</span>
            </a-button>
          </a-col>
        </a-row>
      </a-col>
    </a-row>
    <a-row class="mt-4">
      <h4 class="text-center my-3">3. Create your link to share on social media, text and email</h4>
      <a-col class="slug-input-wrapper" :xs="{ span: 24, offset: 0 }" :sm="{ span: 20, offset: 2 }" :md="{ span: 16, offset: 4 }" :xl="{ span: 12, offset: 6 }">
        <a-input
          :addon-before="urlPrefix"
          size="default"
          v-model="slug"
          pattern="^[a-z0-9]+(?:-[a-z0-9]+)*$"
          onkeypress="return event.charCode != 32"
          @keyup="onKeyUp"
          style="text-transform: lowercase;"
          placeholder="Input your choice here"
        />
      </a-col>
    </a-row>
    <a-row class="mt-3 mb-4">
      <a-col :span="12" class="text-right" style="padding-right: 3rem;">
        <a-popconfirm title="Are you sure" ok-text="Yes" cancel-text="No" @confirm="$emit('onRedo')">
          <a-button type="primary" class="action-button tf-btn tf-btn-primary">
            <span>{{ $t('button.redoAll') }}</span>
          </a-button>
        </a-popconfirm>
      </a-col>
      <a-col :span="12" class="text-left" style="padding-left: 3rem;">
        <a-button type="primary" class="action-button tf-btn tf-btn-primary" @click.prevent="onKeep">
          <span>{{ $t('button.keepAll') }}</span>
        </a-button>
      </a-col>
    </a-row>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { checkValidVideoUrl, checkValidSlug, convertVideoToEmbed } from '@/utils';
import Constants from '@/constants.js';

const readFileAsDataURL = file =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = ev => resolve(ev.target.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

export default {
  props: {
    type: {
      type: String,
      default: 'welcome',
    },
    videoUrl: {
      type: String,
    },
    videoSlug: {
      type: String,
    },
  },
  data() {
    return {
      embedUrl: this.videoUrl,
      slug: this.videoSlug,
      thumbnail: '',
    };
  },
  computed: {
    ...mapGetters(['theme']),
    urlPrefix() {
      return `${Constants.APP_URL}/${this.type}/`;
    },
    correctEmbedUrl() {
      return checkValidVideoUrl(this.embedUrl) ? this.embedUrl : null;
    },
    videoSubtitle() {
      return this.type === 'welcome' ? 'Welcome Video' : 'Jesus Story';
    },
    introVideo() {
      return this.instructionVideo(this.type === 'welcome' ? 'WELCOME_INSTRUCTION_LINK' : 'SHARE_STORY_INSTRUCTION_LINK');
    },
    embedCode() {
      if (!this.correctEmbedUrl) {
        return null;
      }

      return `<iframe src="${convertVideoToEmbed(
        this.correctEmbedUrl,
      )}" style="left:0;top:0;height:100%;width:100%;position:absolute;" frameborder="0" allow="autoplay; fullscreen" allowfullscreen></iframe>`;
    },
  },
  methods: {
    ...mapActions({
      openInstructionVideo: 'openInstructionVideo',
    }),
    onKeep() {
      if (!checkValidSlug(this.slug)) {
        this.$notification['warning']({
          message: 'You must only use lower case letters, no spaces or other characters are accepted.',
        });

        return;
      }

      if (!checkValidVideoUrl(this.embedUrl)) {
        this.$notification['warning']({
          message: 'Embed video URL is wrong!',
        });

        return;
      }

      this.$emit('onEmbedVideoDone', this.slug, this.embedUrl, this.thumbnail);
    },
    onKeyUp(event) {
      this.slug = event.target.value.toLowerCase();
    },
    onSelect() {
      this.$refs.thumbnailInput.click();
    },
    async thumbnailChanged(e) {
      const selected = e.target.files[0];
      if (!selected) {
        return;
      }

      this.thumbnail = await readFileAsDataURL(selected);
    },
  },
};
</script>

<style lang="scss" scoped>
.responsive-embed {
  background-color: transparent;
  border-radius: 0px;
  box-shadow: none;
  margin: 16px;

  &.placeholder {
    background-color: #d7d7d7;
    border-radius: 5px;
  }
}

/deep/ .vp-preview {
  border-radius: 15px !important;
}

.thumbnail-area {
  margin-top: -16px;
  min-height: 120px;
}

.thumbnail-input {
  position: absolute;
  width: 0;
  height: 0;
  visibility: hidden;
  pointer-events: none;
}

.action-button.take-thumbnail {
  width: auto !important;
}

.w-full {
  width: 100%;
}

.open-instruction-video {
  display: flex;
  align-items: center;
  line-height: 16px;
  font-size: 14px;
  font-weight: 600;
  color: #637381;
  float: right;
  padding: 16px 0;
  text-decoration: none;

  & > span {
    margin-left: 8px;
  }
}

.slug-input-wrapper {
  font-size: 16px;
  display: flex;
  align-items: center;

  .slug-input-label {
    white-space: nowrap;
    flex: 0 0 auto;
    font-weight: 600;
  }

  & > .ant-input-group-wrapper,
  & > .ant-input {
    margin-left: 8px;
    flex: 1 1 auto;
  }
}

@media (max-width: 575px) {
  .slug-input-wrapper {
    flex-direction: column;
    align-items: start;

    & > .ant-input-group-wrapper,
    & > .ant-input {
      margin-left: 0;
    }
  }

  .thumbnail-area {
    min-height: 0px;
  }
}

.action-button {
  width: 120px;
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25) !important;
}
</style>
